"use client";

import React from "react";

import { faCheck, faExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";

import { StepStatus, WizardStepperProps } from "./types";

import { useWizard } from "./hooks";

import { cn } from "../../../lib/utils";

import { ButtonStandard } from "../../atoms/ButtonStandard";
import { Icon } from "../../atoms/Icon";
import { Text } from "../../atoms/Text";

const stepCircleStyles: Record<StepStatus, string> = {
	active: "bg-primary-5 text-neutral-0",
	completed: "bg-primary-5 text-neutral-0",
	error: "bg-red-5 text-neutral-0",
	upcoming: "border border-solid border-primary-5 text-primary-5",
};

/**
 * WizardStepper Component
 *
 * Visual progress indicator showing all steps in the wizard.
 * Clicking any visited/completed step validates the current step before navigating.
 */
export const WizardStepper = ({
	currentStep: currentStepProp,
	title,
	completedSteps: completedStepsProp,
	dangerouslySetClassName,
	onExit,
}: WizardStepperProps) => {
	const wizardContext = useWizard();

	const steps = wizardContext.steps;
	const currentStep = currentStepProp ?? wizardContext.currentStep;
	const completedSteps = completedStepsProp ?? wizardContext.completedSteps;
	const { visitedSteps, getStepStatus, handleStepperClick } = wizardContext;

	const canClickStep = (index: number): boolean => {
		if (index === currentStep) return false;
		return completedSteps.includes(index) || visitedSteps.includes(index);
	};

	return (
		<div
			className={cn("box-border flex h-full w-[263px] flex-col bg-neutral-1 px-8 py-6", dangerouslySetClassName)}
			data-slot="wizard-stepper"
		>
			{title && (
				<div className="mb-8">
					{typeof title === "string" ? (
						<Text size="paragraph-1" weight="semi-bold">
							{title}
						</Text>
					) : (
						title
					)}
				</div>
			)}
			<div className="flex-1">
				{steps.map((step, index) => {
					const status = getStepStatus(index);
					const isActive = index === currentStep;
					const isCompleted = completedSteps.includes(index);
					const isClickable = canClickStep(index);

					return (
						<React.Fragment key={step.id}>
							<button
								type="button"
								className={cn(
									"flex h-10 w-full flex-row items-center gap-2 rounded-2 border-none bg-transparent px-2 text-left outline-hidden",
									isActive && "bg-neutral-0",
									isClickable ? "cursor-pointer hover:bg-neutral-0/50" : "cursor-default"
								)}
								disabled={!isClickable}
								onClick={() => handleStepperClick(index)}
								aria-current={isActive ? "step" : undefined}
								aria-label={`Step ${index + 1}: ${step.title}`}
							>
								{/* Step Circle */}
								<div
									className={cn(
										"flex flex-shrink-0 items-center justify-center rounded-full transition-all",
										"h-6 w-6 text-sm font-medium",
										stepCircleStyles[status]
									)}
								>
									{status === "error" ? (
										<Icon color="neutral-0" icon={faExclamation} size={12} />
									) : isCompleted && !isActive ? (
										<Icon color="neutral-0" icon={faCheck} size={16} />
									) : (
										<Text size="caption">{index + 1}</Text>
									)}
								</div>

								{/* Step Title */}
								<Text
									size="paragraph-3"
									weight={isActive || status === "error" ? "semi-bold" : "regular"}
									color="primary-8"
									dangerouslySetClassName="text-left"
								>
									{step.title}
								</Text>
							</button>

							{/* Connector Line */}
							{index < steps.length - 1 && (
								<div className={cn("my-2 ml-5 h-10 w-[1px] bg-neutral-3")} aria-hidden="true" />
							)}
						</React.Fragment>
					);
				})}
			</div>
			{onExit && (
				<ButtonStandard color="neutral" onClick={onExit}>
					Exit Wizard
				</ButtonStandard>
			)}
		</div>
	);
};

WizardStepper.displayName = "WizardStepper";
